import styles from './Login.module.css';
import { useState, useEffect, useRef, useId } from 'react';
import api from '../API.js';
import { useAuth } from '../providers/AuthContext';
import { useMutation } from '@tanstack/react-query';
import EmailAuthLoginSection from './EmailAuthLoginSection';

function AccountInfo({ setExpanded, title = 'Enter your login information', showBackdrop = true }) {
    const idPrefix = useId();
    const { user, oauth, doLogin, doLogout } = useAuth();

    const restartSystemMutation = useMutation({
        mutationFn: () => api.put('system/restart'),
    });

    const [disabled, setDisabled] = useState(false);
    const [status, setStatus] = useState('initial');

    const onSubmit = (event) => {
        event.preventDefault();
        const data = Object.fromEntries(new FormData(event.target));
        doLogin(data.username, data.password).then((success) => {
            setStatus(success ? 'true' : 'false');
            setDisabled(false);
            if (success && setExpanded) {
                setExpanded(false);
            }
        });
        setStatus('pending');
        setDisabled(true);
        return false;
    };

    const loginResponse = (statusVal) => {
        if (statusVal === 'true' || statusVal === 'initial') {
            return null;
        }
        if (statusVal === 'false') {
            return (
                <div className={styles.loginFailed} role="alert">
                    Incorrect username/password
                </div>
            );
        }
        if (statusVal === 'pending') {
            return (
                <div className={styles.loginPending}>
                    Pending...
                </div>
            );
        }
        return <div>Unknown state</div>;
    };

    const loggedOut = (
        <>
            {showBackdrop && !setExpanded && <div className={styles.background} aria-hidden />}
            <div className={!setExpanded && showBackdrop ? styles.loginDialog : styles.dropdownContent}>
                <h3>{title}</h3>
                <form onSubmit={onSubmit} aria-busy={disabled}>
                    <div className={styles.loginInputs}>
                        <label htmlFor={`${idPrefix}-username`}>Username:</label>
                        <input
                            type="text"
                            id={`${idPrefix}-username`}
                            name="username"
                            placeholder="Username"
                            required
                            autoComplete="username"
                        />
                        <label htmlFor={`${idPrefix}-password`}>Password:</label>
                        <input
                            type="password"
                            id={`${idPrefix}-password`}
                            name="password"
                            placeholder="Password"
                            required
                            autoComplete="current-password"
                        />
                    </div>
                    <div aria-live="polite">
                        {loginResponse(status)}
                    </div>
                    <button
                        type="submit"
                        className={styles.submitButton}
                        disabled={disabled}
                    >
                        {disabled ? 'Logging in...' : 'Log in'}
                    </button>
                </form>
                <div className={styles.secondaryLinks}>
                    <a href="/dsa#?dialog=register">Create account</a>
                    {' | '}
                    <a href="/dsa#?dialog=resetpassword">Forgot password?</a>
                </div>
                {oauth.length > 0 && (
                    <>
                        <hr />
                        <div className={styles.oauthHeader}>Or, log in via:</div>
                        {oauth.map((provider) => (
                            <a
                                key={provider.id}
                                href={provider.url}
                                className={styles.oauthButton}
                            >
                                {provider.name}
                            </a>
                        ))}
                    </>
                )}
                <EmailAuthLoginSection variant="login" />
            </div>
        </>
    );

    const loggedIn = (
        <div className={setExpanded ? styles.logoutDialog : (!setExpanded && showBackdrop ? styles.loginDialog : styles.dropdownContent)}>
            <div className={styles.loggedInAs}>
                <span className={styles.loggedInLabel}>Logged in as:</span>{' '}
                <span>{user?.login}</span>
                {user?.email && <span> ({user.email})</span>}
            </div>
            <button type="button" onClick={() => doLogout()} className={styles.logoutButton}>
                Log out
            </button>
            <a href={`/dsa/#useraccount/${user?._id}/info`} className={styles.accountLink}>
                View my account
            </a>
            <hr className={styles.divider} />
            {user?.admin && (
                <button
                    type="button"
                    onClick={() => restartSystemMutation.mutate()}
                    disabled={restartSystemMutation.isPending}
                    className={styles.restartButton}
                >
                    {restartSystemMutation.isPending ? 'Restarting...' : 'Restart the DSA'}
                </button>
            )}
        </div>
    );

    return <>{user?._id ? loggedIn : loggedOut}</>;
}

const FOCUSABLE_SELECTOR = 'input:not([disabled]), button:not([disabled]), a[href], select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function getFocusables(container) {
    if (!container) return [];
    return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR));
}

function LoginDropdown({ expanded, setExpanded, title, triggerRef }) {
    const panelRef = useRef(null);

    useEffect(() => {
        function cancelDialog() {
            setExpanded(false);
        }
        window.addEventListener('click', cancelDialog);
        return () => window.removeEventListener('click', cancelDialog);
    }, [setExpanded]);

    useEffect(() => {
        if (!expanded || !panelRef.current) return;
        const timer = setTimeout(() => {
            const first = panelRef.current.querySelector('input, button, a[href]');
            first?.focus();
        }, 0);
        return () => clearTimeout(timer);
    }, [expanded]);

    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            e.preventDefault();
            setExpanded(false);
            triggerRef?.current?.focus();
            return;
        }
        if (e.key !== 'Tab') return;
        const panel = panelRef.current;
        if (!panel) return;
        const focusables = getFocusables(panel);
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement;
        if (e.shiftKey) {
            if (active === first) {
                e.preventDefault();
                last.focus();
            }
        } else {
            if (active === last) {
                e.preventDefault();
                first.focus();
            }
        }
    };

    const dropdownClick = (e) => e.stopPropagation();

    if (!expanded) return null;

    return (
        <>
            <div
                className={styles.background}
                aria-hidden
                onClick={() => setExpanded(false)}
                onKeyDown={handleKeyDown}
            />
            <div
                className={styles.dropdown}
                onClick={dropdownClick}
                onKeyDown={handleKeyDown}
                ref={panelRef}
                role="dialog"
                aria-label="Login"
            >
                <AccountInfo setExpanded={setExpanded} title={title} />
            </div>
        </>
    );
}

function LoginWidget({ alwaysOpen, title, buttonClassName }) {
    const [expanded, setExpanded] = useState(alwaysOpen);
    const triggerRef = useRef(null);
    const { user } = useAuth();

    const buttonClasses = [
        styles.loginbutton,
        expanded && styles.expanded,
        user?._id && styles.loggedIn,
        buttonClassName,
    ].filter(Boolean).join(' ');

    const buttonClick = (e) => {
        e.stopPropagation();
        setExpanded(!expanded);
    };

    if (alwaysOpen) {
        return (
            <div className={styles.alwaysOpenCard}>
                <AccountInfo setExpanded={false} title={title} showBackdrop={false} />
            </div>
        );
    }

    return (
        <div className={styles.widget}>
            <button
                type="button"
                ref={triggerRef}
                className={buttonClasses}
                onClick={buttonClick}
                aria-expanded={expanded}
                aria-haspopup="dialog"
                aria-label={user?._id ? `Logged in as ${user.login}` : 'Log in'}
            >
                <span className={styles.username}>{user?.login}</span>
                <span className={styles.expand} aria-hidden />
            </button>
            <LoginDropdown
                expanded={expanded}
                setExpanded={setExpanded}
                title={title}
                triggerRef={triggerRef}
            />
        </div>
    );
}

export default LoginWidget;
